/**
 * Floating windows (property drops, link and notes cards, the feed, the panels menu)
 * carry `data-vp-dismiss`. A press on the canvas while one of them is open only
 * closes it. The selection changes on a later press, once every such window is gone.
 *
 * `pointerdown` is not the whole click: the compatibility mouse events still fire
 * afterwards, and by then the window may already have been removed. The rest of
 * that gesture is swallowed too, so it cannot clear the selection.
 */
const DISMISS = '[data-vp-dismiss]';

let installed = false;
/** Nonzero while the gesture that dismisses a window is still finishing. */
let gesture = 0;

function onCanvas(e: Event): boolean {
  const t = e.target;
  return t instanceof Element && !!t.closest('.canvas-host');
}

export function dismissWindowOpen(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector(DISMISS);
}

function swallow(e: Event): void {
  e.preventDefault();
  e.stopPropagation();
}

/** Close-handler helper: a canvas press must not reach the editor. */
export function keepCanvasSelection(e: Event): void {
  if (!onCanvas(e)) return;
  gesture = 1;
  swallow(e);
}

function onPointerDown(e: Event): void {
  if (!onCanvas(e) || !dismissWindowOpen()) return;
  gesture = 1;
  swallow(e);
}

function onRest(e: Event): void {
  if (!gesture || !onCanvas(e)) return;
  swallow(e);
}

function onClick(e: Event): void {
  onRest(e);
  gesture = 0;
}

export function installDismissGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('mousedown', onRest, true);
  window.addEventListener('pointerup', onRest, true);
  window.addEventListener('mouseup', onRest, true);
  window.addEventListener('pointercancel', onClick, true);
  window.addEventListener('click', onClick, true);
}
